import React, { useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useMediaState, type MenuPlacement, type TooltipPlacement } from "@vidstack/react";
import { Menu } from '@vidstack/react';
import { BraivDownArrowIcon, MoreOptionsIcon } from "@/components/player/layouts/shared/playerIcons";
import { usePlayerLanguageContext } from "@/components/player/language/PlayerLanguageContext";
import { getChooseLanguageText, LanguageTrackList } from "@/components/player/language/LanguageTrack";
import LanguageMenuContent from "@/components/player/language/LanguageMenuContent";
export interface LanguageSwitcherProps {
  size: 'small' | 'large';
  placement: MenuPlacement;
  tooltipPlacement: TooltipPlacement;
  portalClass?: string;
  showPortal?: boolean;
  showAsDropdown: boolean;
  displayFlags: boolean;
}
export function getFirstFlags(languages: LanguageTrackList, numFlags: number = 3): LanguageTrackList {
  const firstThree: LanguageTrackList = [];
  languages.forEach(lang => {
    if (firstThree.length < numFlags && lang.countryCode) {
      firstThree.push(lang);
    }
  });
  return firstThree;
}
export function calculateMenuOffset(languages: LanguageTrackList): number {
  if (languages.length === 1) {
    return 0;
  }
  return 0 - (languages.length * 40 + 32 / 2) / 2;
}
export function LanguageSwitcher({
  size = 'large',
  tooltipPlacement,
  placement,
  showPortal,
  portalClass,
  showAsDropdown,
  displayFlags
}: LanguageSwitcherProps) {
  const {
    languageTracks,
    selectedLanguage,
    setSelectedLanguage
  } = usePlayerLanguageContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Detect language preferences from browser
  const chooseLanguageText = useMemo(() => {
    return getChooseLanguageText();
  }, []);
  const onLanguageChange = (lang: string) => {
    console.log('Switching languages to', lang);
    setSelectedLanguage(lang);
  };
  const onMenuOpen = () => {
    setIsMenuOpen(prevState => !prevState);
  };
  const firstFlags = useMemo(() => getFirstFlags(languageTracks, size == 'small' ? 1 : 3), [languageTracks, size]);
  const menuOffset = useMemo(() => calculateMenuOffset(firstFlags), [firstFlags]);

  // Find the currently selected language track to get its country code
  const selectedLanguageTrack = useMemo(() => languageTracks.find(track => track.languageCode === selectedLanguage), [languageTracks, selectedLanguage]);

  //This is the definition of the contents of the dropdown menu
  //Seperated out as it may be conditionally displayed in the portal or inline
  const viewType = useMediaState('viewType');
  if (languageTracks.length <= 1) return null;
  return <Menu.Root className="vds-menu" data-sentry-element="unknown" data-sentry-component="LanguageSwitcher" data-sentry-source-file="LanguageSwitcher.tsx">
            <div className={`${size == 'small' ? 'language-switcher-small' : 'language-switcher'}`}>

                {showAsDropdown ? <Menu.Button className={"vds-menu-button vds-button " + (size === "small" ? "language-switcher-dropdown-small" : "language-switcher-dropdown")} aria-label="Language Options" aria-expanded={isMenuOpen} onClick={() => onMenuOpen()}>
                            {size === "small" && selectedLanguageTrack?.countryCode ? <div className="flag-wrapper">
                                    <ReactCountryFlag className="active-flag-small" countryCode={selectedLanguageTrack.countryCode} svg />
                                    <BraivDownArrowIcon className="braiv-down-arrow-small" />
                                </div> : <span style={{
          fontSize: '0.825rem'
        }}>{chooseLanguageText}</span>}
                        </Menu.Button> : <>
                        <div className="flag-container">
                            {firstFlags.map(e => <ReactCountryFlag key={e.languageCode} className={`${size == 'small' ? 'flag-small' : 'flag'}` + (selectedLanguage == e.languageCode ? " flag-selected" : " flag-not-selected")} onClick={() => onLanguageChange(e.languageCode)} role='button' countryCode={e.countryCode || ''} svg />)}
                            <Menu.Button className="vds-menu-button vds-button" aria-label="More" aria-expanded={isMenuOpen} onClick={() => onMenuOpen()}>
                                <MoreOptionsIcon className="vds-icon" />
                            </Menu.Button>
                        </div>
                    </>}
            </div>
            <Menu.Portal className={`${portalClass ? portalClass : ''}`} disabled={"fullscreen"} data-view-type={viewType} data-sentry-element="unknown" data-sentry-source-file="LanguageSwitcher.tsx">
                <Menu.Items className=" vds-language-menu vds-settings-menu-items vds-menu-items" placement={!showPortal ? placement : null} //If we want to show the portal set the placement to null
      alignOffset={!(showPortal || showAsDropdown) ? menuOffset : 0} data-sentry-element="unknown" data-sentry-source-file="LanguageSwitcher.tsx">
                    <LanguageMenuContent languageTracks={languageTracks} selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} displayFlags={displayFlags} data-sentry-element="LanguageMenuContent" data-sentry-source-file="LanguageSwitcher.tsx" />
                </Menu.Items>
            </Menu.Portal>
        </Menu.Root>;
}